import React, { useEffect, useRef, useState } from 'react';

/**
 * Modern Visual & Math Captcha Component
 * Generates an anti-bot stylized security code with canvas distortion and instant reload.
 */
export default function Captcha({ onChange, onValidate, error = false, className = '' }) {
  const [captchaCode, setCaptchaCode] = useState('');
  const [userInput, setUserInput] = useState('');
  const canvasRef = useRef(null);

  // Generate a random 5-character alphanumeric string (excluding confusing chars like 0/O, 1/I)
  const generateCaptcha = () => {
    const chars = '23456789ABCDEFGHJKLMNPQRSTUVWXYZ';
    let code = '';
    for (let i = 0; i < 5; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setCaptchaCode(code);
    setUserInput('');
    if (onChange) onChange('');
    if (onValidate) onValidate(false);
    drawCaptcha(code);
  };

  const drawCaptcha = (code) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;

    // Background gradient
    const gradient = ctx.createLinearGradient(0, 0, width, height);
    gradient.addColorStop(0, '#FAF8F5');
    gradient.addColorStop(1, '#F3ECE4');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);

    // Add noise lines
    for (let i = 0; i < 4; i++) {
      ctx.strokeStyle = ['#C49A4540', '#8A796C30', '#B0873640', '#D9D0C8'][i % 4];
      ctx.lineWidth = Math.random() * 2 + 1;
      ctx.beginPath();
      ctx.moveTo(Math.random() * width, Math.random() * height);
      ctx.bezierCurveTo(
        Math.random() * width, Math.random() * height,
        Math.random() * width, Math.random() * height,
        Math.random() * width, Math.random() * height
      );
      ctx.stroke();
    }

    // Add noise dots
    for (let i = 0; i < 35; i++) {
      ctx.fillStyle = ['#C49A4550', '#8A796C40', '#2B181030'][i % 3];
      ctx.beginPath();
      ctx.arc(Math.random() * width, Math.random() * height, Math.random() * 1.5, 0, Math.PI * 2);
      ctx.fill();
    }

    // Draw characters with distinct fonts, rotation, and colors
    const colors = ['#2B1810', '#8C6B28', '#3D281D', '#9D772D', '#1C100A'];
    const letterSpacing = (width - 24) / code.length;

    for (let i = 0; i < code.length; i++) {
      const char = code[i];
      ctx.save();
      const x = 16 + i * letterSpacing;
      const y = height / 2 + Math.random() * 6 - 3;
      const angle = (Math.random() - 0.5) * 0.4; // slight tilt

      ctx.translate(x, y);
      ctx.rotate(angle);
      ctx.font = 'bold 22px Georgia, serif';
      ctx.fillStyle = colors[i % colors.length];
      ctx.textBaseline = 'middle';
      ctx.fillText(char, 0, 0);
      ctx.restore();
    }
  };

  useEffect(() => {
    generateCaptcha();
  }, []);

  const handleInputChange = (e) => {
    const val = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 5);
    setUserInput(val);
    if (onChange) onChange(val);
    if (onValidate) onValidate(val === captchaCode);
  };

  return (
    <div className={`space-y-2 ${className}`}>
      <div className="flex items-center justify-between">
        <label className="text-xs font-bold text-[#2B1810] flex items-center gap-1.5">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#C49A45" strokeWidth="2.5">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
          <span>Security Verification (CAPTCHA)</span>
        </label>
        <span className="text-[10px] text-[#8A796C]">Case-insensitive</span>
      </div>

      <div className="flex items-center gap-2">
        {/* Captcha Canvas Image */}
        <div className={`relative rounded-lg border overflow-hidden bg-[#FAF8F5] shrink-0 shadow-2xs ${
          error ? 'border-red-500 ring-1 ring-red-500' : 'border-[#D9D0C8]'
        }`}>
          <canvas
            ref={canvasRef}
            width={140}
            height={42}
            className="block cursor-pointer select-none"
            title="Click to reload captcha"
            onClick={generateCaptcha}
          />
        </div>

        {/* Refresh Button */}
        <button
          type="button"
          onClick={generateCaptcha}
          className="w-10 h-10 rounded-lg border border-[#D9D0C8] bg-white hover:bg-[#FAF8F5] text-[#8A796C] hover:text-[#C49A45] flex items-center justify-center transition-all shrink-0 active:scale-95 shadow-2xs"
          title="Refresh Captcha"
          aria-label="Reload security code"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.57-8.38l5.67-5.67" />
          </svg>
        </button>

        {/* User Input Field */}
        <input
          type="text"
          value={userInput}
          onChange={handleInputChange}
          placeholder="ENTER CODE"
          maxLength={5}
          className={`flex-1 min-w-0 h-10 px-3 text-xs font-mono font-bold tracking-widest uppercase rounded-lg border bg-white text-[#2B1810] placeholder-[#A0938A] focus:outline-none transition-all ${
            error
              ? 'border-red-500 bg-red-50/20 focus:border-red-500 focus:ring-1 focus:ring-red-500'
              : 'border-[#D9D0C8] focus:border-[#C49A45] focus:ring-1 focus:ring-[#C49A45]'
          }`}
        />
      </div>

      {userInput && (
        <p className={`text-[10.5px] font-semibold flex items-center gap-1 ${
          userInput === captchaCode ? 'text-emerald-700' : 'text-amber-700'
        }`}>
          {userInput === captchaCode ? (
            <>
              <span className="font-bold">✓</span> Captcha verified
            </>
          ) : (
            <>
              <span>ℹ</span> Enter the exact 5-character code shown above
            </>
          )}
        </p>
      )}
    </div>
  );
}
